import React, { useState } from 'react';
import { Button } from '@heroui/react';
import { Input } from '@heroui/input';
import { Textarea } from '@heroui/input';
import Close from '@/icons/Close';
import { textareaClassNames } from '@/components/ui/design';

interface ReportEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reportData: ReportEventData) => void;
    isSubmitting?: boolean;
}

export interface ReportEventData {
    reason: string;
    description: string;
}

const REPORT_REASONS = [
    'Spam or fraud',
    'Sexual content',
    'Violence or dangerous organizations',
    'Event canceled',
    'Hate speech',
    'Something else'
];

export default function ReportEventModal({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false
}: ReportEventModalProps) {
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [description, setDescription] = useState<string>('');

    const handleSubmit = () => {
        if (!selectedReason) return;

        // Validate that description is provided for "Something else"
        if (selectedReason === 'Something else' && !description.trim()) {
            return; // Don't submit if description is empty
        }

        onSubmit({
            reason: selectedReason,
            description: selectedReason === 'Something else' ? description : ''
        });
    };

    const handleClose = () => {
        setSelectedReason('');
        setDescription('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-nightElevated rounded p-6 max-w-md w-full mx-4 shadow-lg transition-colors">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-nightTextSecondary">Report this event</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 dark:text-nightMuted hover:text-gray-600 dark:hover:text-white transition-colors"
                        disabled={isSubmitting}
                    >

                        <Close width={24} height={24} />


                    </button>
                </div>

                {/* Introduction text */}
                <p className="text-sm text-gray-600 dark:text-nightMuted mb-4">
                    If you have a question about an event, need to resolve a dispute, or wish to request a refund, we encourage you to first <strong>contact the organizer</strong> directly.
                </p>

                {/* Report reasons */}
                <div className="space-y-3 mb-4">
                    {REPORT_REASONS.map((reason) => (
                        <label key={reason} className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="reportReason"
                                value={reason}
                                checked={selectedReason === reason}
                                onChange={(e) => setSelectedReason(e.target.value)}
                                className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                                disabled={isSubmitting}
                            />
                            <span className="text-sm text-gray-700 dark:text-nightTextSecondary">{reason}</span>
                        </label>
                    ))}
                </div>

                {/* Description textarea - only show for "Something else" */}
                {selectedReason === 'Something else' && (
                    <div className="mb-4">
                        <Textarea
                            placeholder="Please describe the reason for your report"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={isSubmitting}
                            className="w-full"
                            classNames={textareaClassNames}
                            minRows={3}
                            maxRows={6}
                        />
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="light"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="text-gray-600 dark:text-nightMuted"
                    >
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleSubmit}
                        disabled={!selectedReason || isSubmitting || (selectedReason === 'Something else' && !description.trim())}
                        className="bg-red-600 text-white font-semibold"
                    >
                        {isSubmitting ? 'Reporting...' : 'Report This Event'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
